import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Loading from 'components/Loading';
import TimeStampType2Card from 'components/Card/TimeStampType2Card';
import { useGeolocated } from 'react-geolocated';
import CalculateGpsByProject from 'utils/functions/CalculateGpsByProject';
import dayjs from 'dayjs';
import {
  Autocomplete,
  Backdrop,
  Card,
  Button,
  Fade,
  Modal,
  TextField,
  Typography,
  Box,
  Stack,
  Avatar,
  Chip,
  Paper,
  Alert,
  AlertTitle,
  Divider,
} from '@mui/material';
import {
  LocationOff as LocationOffIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  GpsOff as GpsOffIcon,
  MyLocation as MyLocationIcon,
  Place as PlaceIcon,
  SendOutlined as SendIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import CalculateGps from '../../utils/functions/CalculateGps';
import CalculateSalaryperday from '../../utils/functions/CalculateSalaryperday';
import TimeStampGPSCard from '../../components/Card/TimeStampGPSCard';
import Cameras from '../../components/Camera/Cameras';
import * as actions from '../../redux/actions';
import TimeStampCard from '../../components/Card/TimeStampCard';

export default function CheckIn({ title, subtitle }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const project = useSelector((state) => state.project);
  const info = useSelector((state) => state.info);
  const holiday = useSelector((state) => state.holiday);
  const history = useHistory();
  const [imgSrc, setImgSrc] = useState(null);
  const [note, setNote] = useState('');
  const [noteCheckin, setNoteCheckin] = useState('');
  const [projectSelect, setProjectSelect] = useState();
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [requestNote, setRequestNote] = useState('');
  const [requestProject, setRequestProject] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestStep, setRequestStep] = useState(1);
  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('unknown');

  const {
    coords,
    isGeolocationAvailable,
    isGeolocationEnabled,
    positionError,
  } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false, // false = ได้ตำแหน่งเร็วขึ้น (ใช้เครือข่าย/เซลล์)
      timeout: 10000, // รอสูงสุด 10 วินาที
      maximumAge: 5 * 60 * 1000, // ใช้ตำแหน่งแคชได้ถ้าไม่เก่าเกิน 5 นาที (ได้ทันทีถ้ามีแคช)
    },
    userDecisionTimeout: 5000,
  });

  const isAdmin = !!me?.userData?.permissions?.admin;
  const requirePhoto = me?.userData?.require_checkin_photo !== false;
  const hasCoords = coords != null;
  const locationLoading =
    isGeolocationEnabled && coords == null && !positionError;

  const useLocationMode =
    (me?.userData?.timestamp_use_location != null
      ? !!me.userData.timestamp_use_location
      : !!info?.setting?.timestamp_location);

  const projectAccessMode = me?.userData?.project_access_mode === true;
  const assignedIds = (me?.userData?.assigned_projects || []).map((id) =>
    id?._id?.toString() || id?.toString());
  const availableProject = projectAccessMode
    ? { ...project, rows: (project?.rows || []).filter((p) => assignedIds.includes(p._id?.toString())) }
    : project;

  const locationCheckIn = CalculateGps(coords, availableProject?.rows, 1000);
  const nearest = locationCheckIn?.nearestProject;
  const nearestProject = nearest?.project;
  const isWithin1Km = nearest?.isNearby === true;
  const isOutOfLocation = hasCoords && !isWithin1Km;
  const nearbyProjects = locationCheckIn?.nearbyProjects || [];
  const hasOverlap = nearbyProjects.length > 1;

  const nearbyProjectIds = new Set(nearbyProjects.map((n) => n.project?._id?.toString()));
  const sortedProjects = [
    ...(availableProject?.rows || []).filter((p) => nearbyProjectIds.has(p._id?.toString())),
    ...(availableProject?.rows || []).filter((p) => !nearbyProjectIds.has(p._id?.toString())),
  ];

  // Auto select only when exactly 1 nearby project (no overlap)
  useEffect(() => {
    if (!nearestProject || !isWithin1Km) return;
    if (hasOverlap) return; // ให้ user เลือกเองเมื่อมีหลายโปรเจคทับซ้อน
    if (projectSelect?._id === nearestProject?._id) return;
    setProjectSelect(nearestProject);
  }, [nearestProject?._id, isWithin1Km, hasOverlap]);

  useEffect(() => {
    dispatch(actions.meGet({}));
    dispatch(actions.getInformation());
    dispatch(actions.projectAll({ time_tracking_available: true, lite: true }));
    dispatch(actions.holidayAll({ date: dayjs().startOf('date') }));
    return () => { };
  }, []);

  useEffect(() => {
    if (!requirePhoto) return;
    if (!navigator?.permissions?.query) return;
    let permResult = null;
    const handler = () => permResult && setCameraPermission(permResult.state);
    navigator.permissions.query({ name: 'camera' }).then((result) => {
      permResult = result;
      setCameraPermission(result.state);
      result.addEventListener('change', handler);
    }).catch(() => {});
    return () => { if (permResult) permResult.removeEventListener('change', handler); };
  }, [requirePhoto]);

  const rate = CalculateSalaryperday(holiday);

  const now = dayjs();
  const isLunchTime = now.hour() === 12 && now.minute() < 30;

  const onSubmit = async () => {
    if (useLocationMode && (isOutOfLocation || !isWithin1Km)) {
      alert('อยู่นอกพื้นที่ 1 กม. ไม่สามารถลงเวลาได้ กรุณาติดต่อแอดมิน');
      return;
    }
    if (isLunchTime) {
      alert('อยู่ในช่วงพักเที่ยง (12:00–12:30 น.)\nกรุณาเช็คอินใหม่อีกครั้งหลัง 12:30 น.');
      return;
    }
    if (!projectSelect) {
      alert(useLocationMode ? 'ไม่พบโครงการในระยะ 1 กม.' : 'กรุณาเลือกโครงการ');
    } else {
      const confirm = window.confirm('save');
      if (confirm) {
        setLoading(true);
        const data = {
          check_in_source: 'profile',
          employee: me?.userData?._id,
          employee_firstname: me?.userData?.firstname,
          employee_lastname: me?.userData?.lastname,
          employeeType: me?.userData?.type,
          salary: me?.userData?.salary,
          salary_extra: me?.userData?.salary_extra,
          status_checkIn: true,
          project_in: projectSelect,
          image: imgSrc,
          locationCheckIn: useLocationMode ? locationCheckIn : undefined,
          rate,
          out_of_location: useLocationMode ? isOutOfLocation : false,
          note: noteCheckin,
        };
        try {
          await dispatch(actions.timestampCreate(data));
          history.goBack();
        } catch (error) {
          const msg = error?.response?.data?.error?.message
            || error?.response?.data?.message
            || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
          alert(msg);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const onSubmitRequest = async () => {
    if (!requestProject) {
      alert('กรุณาเลือกโครงการ');
      return;
    }
    if (!requestNote.trim()) {
      alert('กรุณาระบุเหตุผล/หลักฐาน');
      return;
    }
    if (!coords) {
      alert('กรุณาเปิด GPS ก่อนส่งคำขอ เพื่อบันทึกพิกัดสำหรับตรวจสอบ');
      return;
    }
    if (requirePhoto && !imgSrc) {
      alert('กรุณาถ่ายรูปเพื่อยืนยันตัวตน');
      return;
    }
    const confirm = window.confirm('ส่งคำขอลงเวลานอกพื้นที่?');
    if (!confirm) return;
    setRequestLoading(true);
    try {
      const data = {
        check_in_source: 'profile',
        employee: me?.userData?._id,
        employee_firstname: me?.userData?.firstname,
        employee_lastname: me?.userData?.lastname,
        employeeType: me?.userData?.type,
        salary: me?.userData?.salary,
        salary_extra: me?.userData?.salary_extra,
        status_checkIn: true,
        project_in: requestProject,
        locationCheckIn: {
          currentLocation: {
            lat: coords.latitude,
            lon: coords.longitude,
          },
        },
        rate,
        out_of_location: true,
        note: requestNote,
        image: imgSrc,
        approval_status: 'PENDING',
      };
      await dispatch(actions.timestampCreate(data));
      setOpenRequestModal(false);
      setRequestNote('');
      setRequestProject(null);
      setImgSrc(null);
      setRequestStep(1);
      alert('ส่งคำขอสำเร็จ — รอ admin อนุมัติก่อนจึงจะนับเป็นการลงเวลา');
      history.goBack();
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'เกิดข้อผิดพลาด กรุณาลองใหม่';
      alert(msg);
    } finally {
      setRequestLoading(false);
    }
  };

  const renderCameraPermissionAlert = () => {
    if (!requirePhoto || cameraPermission !== 'denied') return null;
    return (
      <Alert severity="error" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>กล้องถูกบล็อก — ลงเวลาไม่ได้</AlertTitle>
        กรุณาเปิดสิทธิ์กล้องให้เว็บไซต์นี้ก่อน แล้วรีโหลดหน้า
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" display="block">🌐 <strong>Chrome/Edge:</strong> กดไอคอน 🔒 ซ้ายของ URL → กล้อง → อนุญาต → รีโหลด</Typography>
          <Typography variant="caption" display="block">🦊 <strong>Firefox:</strong> กดไอคอน 🔒 ซ้ายของ URL → ล้างการอนุญาต → รีโหลด</Typography>
          <Typography variant="caption" display="block">🍎 <strong>Safari/iPhone:</strong> ตั้งค่า → Safari → กล้อง → อนุญาต</Typography>
        </Box>
      </Alert>
    );
  };

  const renderRequestModal = () => {
    const closeModal = () => {
      setOpenRequestModal(false);
      setImgSrc(null);
      setRequestStep(1);
    };

    const goToStep2 = () => {
      if (!requestProject) { alert('กรุณาเลือกโครงการ'); return; }
      if (!requestNote.trim()) { alert('กรุณาระบุเหตุผล/หลักฐาน'); return; }
      if (!requirePhoto) { onSubmitRequest(); return; }
      setImgSrc(null);
      setRequestStep(2);
    };

    return (
      <Modal
        open={openRequestModal}
        onClose={closeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={openRequestModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '92%', sm: 460 },
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                p: 2.5,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {requestStep === 1 ? <LocationOffIcon /> : <PhotoCameraIcon />}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {requestStep === 1 ? 'ขอลงเวลานอกพื้นที่' : 'ถ่ายรูปยืนยันตัวตน'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {requestStep === 1
                    ? 'ระบุโครงการและเหตุผลเพื่อให้ผู้บริหารตรวจสอบ'
                    : 'ถ่ายรูปเพื่อยืนยันตัวตน แล้วกดส่งคำขอ'}
                </Typography>
              </Box>
              <Chip
                label={`${requestStep}/2`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }}
              />
            </Box>

            {/* Step 1: Form */}
            {requestStep === 1 && (
              <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                  การลงเวลาจะถูกบันทึกว่า <strong>นอกพื้นที่</strong> และผู้บริหารจะสามารถตรวจสอบได้
                </Alert>

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  โครงการ *
                </Typography>
                <Autocomplete
                  options={sortedProjects}
                  value={requestProject}
                  onChange={(_, v) => setRequestProject(v)}
                  getOptionLabel={(o) =>
                    typeof o === 'string'
                      ? o
                      : `${o.project_number || ''} | ${o.name || ''}`
                  }
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  groupBy={(o) => nearbyProjectIds.has(o._id?.toString()) ? '📍 ในพื้นที่' : 'โครงการอื่นๆ'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="เลือกโครงการ"
                      sx={{ mb: 2 }}
                    />
                  )}
                />

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  เหตุผล / หลักฐาน *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="เช่น ปฏิบัติงานนอกสถานที่ตามคำสั่ง, ไปส่งงานที่ไซต์อื่น ฯลฯ"
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  sx={{ mb: 2.5 }}
                />

                {/* GPS Status */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: coords ? 'success.50' : locationLoading ? 'grey.100' : 'error.50',
                    border: '1px solid',
                    borderColor: coords ? 'success.light' : locationLoading ? 'grey.300' : 'error.light',
                  }}
                >
                  <MyLocationIcon
                    fontSize="small"
                    color={coords ? 'success' : locationLoading ? 'disabled' : 'error'}
                  />
                  <Box sx={{ flex: 1 }}>
                    {coords ? (
                      <>
                        <Typography variant="caption" color="success.main" fontWeight={600} display="block">
                          GPS พร้อมใช้งาน
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}
                        </Typography>
                      </>
                    ) : locationLoading ? (
                      <Typography variant="caption" color="text.secondary">กำลังรับสัญญาณ GPS...</Typography>
                    ) : (
                      <>
                        <Typography variant="caption" color="error.main" fontWeight={600} display="block">
                          ไม่พบ GPS — กรุณาเปิดการใช้ตำแหน่งในเบราว์เซอร์
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          จำเป็นต้องมี GPS เพื่อตรวจสอบพิกัดย้อนหลัง
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button variant="outlined" fullWidth onClick={closeModal}>
                    ยกเลิก
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={requirePhoto ? <PhotoCameraIcon /> : <SendIcon />}
                    onClick={goToStep2}
                    disabled={!coords || requestLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #e55a5a 0%, #d44e1e 100%)' },
                      '&.Mui-disabled': { opacity: 0.5 },
                    }}
                  >
                    {requestLoading ? 'กำลังส่ง...' : requirePhoto ? 'ถ่ายรูปลงเวลา' : 'ส่งคำขอ'}
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Step 2: Camera */}
            {requestStep === 2 && (
              <Box sx={{ p: 3 }}>
                {renderCameraPermissionAlert()}
                {imgSrc && (
                  <Alert severity="success" sx={{ mb: 2, mt: cameraPermission === 'denied' ? 2 : 0, borderRadius: 2 }}>
                    ถ่ายรูปเรียบร้อย — กด <strong>ส่งคำขอ</strong> เพื่อยืนยัน
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5, mt: imgSrc || cameraPermission === 'denied' ? 2 : 0 }}>
                  <Cameras imgSrc={imgSrc} setImgSrc={setImgSrc} />
                </Box>

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { setImgSrc(null); setRequestStep(1); }}
                    disabled={requestLoading}
                  >
                    ย้อนกลับ
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SendIcon />}
                    onClick={onSubmitRequest}
                    disabled={requestLoading || (requirePhoto && !imgSrc)}
                    sx={{
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #e55a5a 0%, #d44e1e 100%)' },
                      '&.Mui-disabled': { opacity: 0.5 },
                    }}
                  >
                    {requestLoading ? 'กำลังส่ง...' : 'ส่งคำขอ'}
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    );
  };

  const renderCard = () => (
    <div>
      {hasOverlap && projectSelect && (
        <Box sx={{ p: 2, pb: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlaceIcon />}
            onClick={() => setProjectSelect(null)}
          >
            เปลี่ยนโครงการ
          </Button>
        </Box>
      )}
      {info?.setting?.timestamp_image && renderCameraPermissionAlert()}
      {info?.setting?.timestamp_image ? (
        <div>
          <TimeStampGPSCard
            me={me}
            date={new Date()}
            imgSrc={imgSrc}
            setImgSrc={setImgSrc}
            noteCheckin={noteCheckin}
            setNoteCheckin={setNoteCheckin}
            project={availableProject}
            projectSelect={projectSelect}
            setProjectSelect={setProjectSelect}
            onSubmit={onSubmit}
            locationCheck={locationCheckIn}
            autoProject
            requirePhoto={requirePhoto}
          />
        </div>
      ) : (
        <TimeStampType2Card
          me={me}
          date={new Date()}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          noteCheckin={noteCheckin}
          setNoteCheckin={setNoteCheckin}
          project={availableProject}
          projectSelect={projectSelect}
          setProjectSelect={setProjectSelect}
          onSubmit={onSubmit}
          autoProject
          locationCheck={locationCheckIn}
          requirePhoto={requirePhoto}
        />
      )}
    </div>
  );

  const renderTimeStampCardWithOutLocation = () => (
    <div>
      {!approved ? (
        <>
          <Box
            sx={{
              minHeight: '100vh',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Card
              variant="outlined"
              sx={{
                maxWidth: 400,
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                bgcolor: 'background.paper',
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                  p: 3,
                  textAlign: 'center',
                  color: 'white',
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <LocationOffIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  นอกพื้นที่ทำงาน
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  ตำแหน่งปัจจุบันของท่านอยู่นอกเขตพื้นที่ปฏิบัติงาน
                </Typography>
              </Box>
              {/* Content Section */}
              <Box sx={{ p: 3 }}>
                <Alert
                  severity="warning"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem',
                    },
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>
                    การลงเวลานอกสถานที่
                  </AlertTitle>
                  กรุณาแนบหลักฐานหรือเหตุผลในการทำงานนอกสถานที่
                  เพื่อให้ผู้บริหารสามารถตรวจสอบและอนุมัติได้
                </Alert>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    borderStyle: 'dashed',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    📋 ตัวอย่างหลักฐาน
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    • รูปถ่ายสถานที่ปฏิบัติงาน
                    <br />
                    • ใบงาน หรือหนังสือนัดหมาย
                    <br />• คำอธิบายสั้นๆ เกี่ยวกับงาน
                  </Typography>
                </Paper>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  {isAdmin && (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={() => setApproved(true)}
                      startIcon={<CheckCircleIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        background:
                          'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        '&:hover': {
                          background:
                            'linear-gradient(135deg, #43a3f5 0%, #00d4e6 100%)',
                        },
                      }}
                    >
                      ดำเนินการลงเวลา (แอดมิน)
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={() => history.goBack()}
                    startIcon={<CancelIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                    }}
                  >
                    ย้อนกลับ
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    icon={<WarningIcon fontSize="small" />}
                    label="บันทึกนอกพื้นที่ — ผู้บริหารจะตรวจสอบ"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Box>
              </Box>{' '}
            </Card>
          </Box>
        </>
      ) : (
        <Box>
          <TimeStampCard
            me={me}
            date={new Date()}
            imgSrc={imgSrc}
            setImgSrc={setImgSrc}
            noteCheckin={noteCheckin}
            setNoteCheckin={setNoteCheckin}
            project={availableProject}
            projectSelect={projectSelect}
            setProjectSelect={setProjectSelect}
            onSubmit={onSubmit}
            locationCheck={locationCheckIn}
            alert={true}
            requirePhoto={requirePhoto}
          />
        </Box>
      )}
    </div>
  );
  const renderProjectSelectModal = () => (
    <Modal
      open={openProjectModal}
      onClose={() => setOpenProjectModal(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={openProjectModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 460 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 2.5, color: 'white', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PlaceIcon />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>เลือกโครงการลงเวลา</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>เลือกโครงการในพื้นที่หรือค้นหาโครงการอื่น</Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ overflowY: 'auto', p: 3 }}>
            {/* โครงการในพื้นที่ */}
            {nearbyProjects.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'block', mb: 1, letterSpacing: 0.5 }}>
                  📍 โครงการในพื้นที่ของคุณ
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  {nearbyProjects.map((item) => (
                    <Paper
                      key={item.project?._id}
                      variant="outlined"
                      sx={{
                        p: 2, borderRadius: 2, cursor: 'pointer', transition: 'all 0.2s',
                        borderColor: projectSelect?._id === item.project?._id ? 'primary.main' : 'primary.light',
                        bgcolor: projectSelect?._id === item.project?._id ? 'primary.50' : 'transparent',
                        '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main', boxShadow: '0 2px 8px rgba(79,172,254,0.3)' },
                      }}
                      onClick={() => { setProjectSelect(item.project); setOpenProjectModal(false); }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <PlaceIcon color="primary" fontSize="small" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} noWrap>{item.project?.project_number || 'ไม่ระบุเลขโครงการ'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{item.project?.name || '-'}</Typography>
                        </Box>
                        <Chip label={item.distanceText} size="small" color="primary" variant="outlined" />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Divider sx={{ mb: 2.5 }}>
                  <Typography variant="caption" color="text.secondary">หรือเลือกโครงการอื่น</Typography>
                </Divider>
              </>
            )}

            {/* Autocomplete ทุกโครงการ */}
            <Autocomplete
              options={sortedProjects}
              getOptionLabel={(o) => typeof o === 'string' ? o : `${o.project_number || ''} | ${o.name || ''}`}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              groupBy={nearbyProjects.length > 0
                ? (o) => nearbyProjectIds.has(o._id?.toString()) ? '📍 ในพื้นที่' : 'โครงการอื่นๆ'
                : undefined}
              onChange={(_, v) => { if (v) { setProjectSelect(v); setOpenProjectModal(false); } }}
              renderInput={(params) => (
                <TextField {...params} size="small" label="ค้นหาโครงการ" placeholder="พิมพ์ชื่อหรือเลขโครงการ..." autoFocus />
              )}
              sx={{ mb: 2 }}
            />

            <Button variant="outlined" fullWidth onClick={() => setOpenProjectModal(false)}>
              ยกเลิก
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const renderOldCard = () => (
    <div>
      {/* ปุ่มเลือก/เปลี่ยนโครงการ */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Paper
          variant="outlined"
          onClick={() => setOpenProjectModal(true)}
          sx={{
            p: 1.5, borderRadius: 2, cursor: 'pointer', transition: 'all 0.2s',
            borderColor: projectSelect ? 'primary.main' : nearbyProjects.length > 0 ? 'primary.light' : 'divider',
            bgcolor: projectSelect ? 'primary.50' : 'background.paper',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PlaceIcon color={projectSelect ? 'primary' : 'disabled'} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {projectSelect ? (
                <>
                  <Typography variant="caption" color="primary.main" fontWeight={700} display="block">โครงการที่เลือก</Typography>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {projectSelect.project_number ? `${projectSelect.project_number} | ` : ''}{projectSelect.name}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {nearbyProjects.length > 0 ? `พบ ${nearbyProjects.length} โครงการในพื้นที่` : 'ยังไม่ได้เลือกโครงการ'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">กดเพื่อเลือกโครงการ</Typography>
                </>
              )}
            </Box>
            <Chip
              label={projectSelect ? 'เปลี่ยน' : 'เลือก'}
              size="small"
              color={projectSelect ? 'primary' : 'default'}
              variant="outlined"
            />
          </Stack>
        </Paper>
      </Box>
      {renderProjectSelectModal()}
      {info?.setting?.timestamp_image && renderCameraPermissionAlert()}
      {info?.setting?.timestamp_image ? (
        <TimeStampCard
          me={me}
          date={new Date()}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          noteCheckin={noteCheckin}
          setNoteCheckin={setNoteCheckin}
          project={availableProject}
          projectSelect={projectSelect}
          setProjectSelect={setProjectSelect}
          onSubmit={onSubmit}
          locationCheck={locationCheckIn}
          useLocationCheck={false}
          requirePhoto={requirePhoto}
        />
      ) : (
        <TimeStampType2Card
          me={me}
          date={new Date()}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          noteCheckin={noteCheckin}
          setNoteCheckin={setNoteCheckin}
          project={availableProject}
          projectSelect={projectSelect}
          setProjectSelect={setProjectSelect}
          onSubmit={onSubmit}
        />
      )}
    </div>
  );

  const renderGPSRequired = () => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
      <Card variant="outlined" sx={{ maxWidth: 380, width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)', p: 3, textAlign: 'center', color: 'white' }}>
          <GpsOffIcon sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>ยังไม่ได้เปิด GPS</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>ไม่สามารถลงเวลาได้</AlertTitle>
            กรุณาเปิดการเข้าถึงตำแหน่ง (GPS) ของอุปกรณ์ก่อนลงเวลาเข้างาน
          </Alert>
          <Stack spacing={2}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<MyLocationIcon />}
              onClick={() => navigator.geolocation.getCurrentPosition(() => { }, () => { })}
              sx={{ py: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)', color: 'white' }}
            >
              เปิดการแจ้งเตือนตำแหน่ง
            </Button>
            <Button variant="outlined" size="large" fullWidth onClick={() => history.goBack()} startIcon={<CancelIcon />} sx={{ py: 1.5, borderRadius: 2 }}>
              ย้อนกลับ
            </Button>
          </Stack>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} sx={{ mb: 1 }}>
              หากปุ่มไม่ทำงาน (เคยกดบล็อกไปแล้ว) ให้ทำตามขั้นตอนนี้:
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              🌐 <strong>Chrome / Edge:</strong> กดไอคอน 🔒 หรือ ⓘ ซ้ายของ URL → ตำแหน่ง → อนุญาต
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              🦊 <strong>Firefox:</strong> กดไอคอน 🔒 ซ้ายของ URL → ล้างการอนุญาต → รีโหลดหน้า
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              🍎 <strong>Safari (iPhone):</strong> ตั้งค่า → Safari → ตำแหน่ง → อนุญาต
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );

  const renderLocationLoading = () => (
    <Box
      sx={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          maxWidth: 380,
          width: '100%',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          กำลังหาตำแหน่ง...
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          เปิดการเข้าถึงตำแหน่งให้แอปแล้วรอสักครู่
          (ใช้ตำแหน่งแคชถ้ามี จะได้เร็วขึ้น)
        </Typography>
      </Card>
    </Box>
  );

  const renderProjectAccessOutOfLocation = () => (
    <div>
      {!approved ? (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Card variant="outlined" sx={{ maxWidth: 400, width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', bgcolor: 'background.paper' }}>
            <Box sx={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)', p: 3, textAlign: 'center', color: 'white' }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)', mx: 'auto', mb: 2 }}>
                <LocationOffIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                คุณอยู่นอกพื้นที่ทำงาน
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                ของโครงการที่คุณมีสิทธิ์
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { fontSize: '1.5rem' } }}>
                <AlertTitle sx={{ fontWeight: 700 }}>การลงเวลานอกสถานที่</AlertTitle>
                ตำแหน่งปัจจุบันของท่านอยู่นอกเขตพื้นที่ปฏิบัติงาน
                กรุณาแนบหลักฐานหรือเหตุผลในการทำงานนอกสถานที่
                เพื่อให้ผู้บริหารสามารถตรวจสอบและอนุมัติได้
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="text.secondary" align="center">📋 ตัวอย่างหลักฐาน</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  • รูปถ่ายสถานที่ปฏิบัติงาน<br />
                  • ใบงาน หรือหนังสือนัดหมาย<br />
                  • คำอธิบายสั้นๆ เกี่ยวกับงาน
                </Typography>
              </Paper>
              <Stack spacing={2}>
                {isAdmin && (
                  <Button variant="contained" size="large" fullWidth onClick={() => setApproved(true)} startIcon={<CheckCircleIcon />}
                    sx={{ py: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', '&:hover': { background: 'linear-gradient(135deg, #43a3f5 0%, #00d4e6 100%)' } }}>
                    ดำเนินการลงเวลา (แอดมิน)
                  </Button>
                )}
                <Button variant="outlined" size="large" fullWidth onClick={() => history.goBack()} startIcon={<CancelIcon />} sx={{ py: 1.5, borderRadius: 2, borderColor: 'grey.300', color: 'text.secondary' }}>
                  ย้อนกลับ
                </Button>
              </Stack>
            </Box>
          </Card>
        </Box>
      ) : (
        <Box>
          <TimeStampCard
            me={me}
            date={new Date()}
            imgSrc={imgSrc}
            setImgSrc={setImgSrc}
            noteCheckin={noteCheckin}
            setNoteCheckin={setNoteCheckin}
            project={availableProject}
            projectSelect={projectSelect}
            setProjectSelect={setProjectSelect}
            onSubmit={onSubmit}
            locationCheck={locationCheckIn}
            alert={true}
            requirePhoto={requirePhoto}
          />
        </Box>
      )}
    </div>
  );

  const noAssignedProjects = projectAccessMode && (availableProject?.rows?.length ?? 0) === 0;

  const renderNoAssignedProjects = () => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card variant="outlined" sx={{ maxWidth: 400, width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 3, textAlign: 'center', color: 'white' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>ยังไม่ได้รับมอบหมายโครงการ</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>ไม่สามารถลงเวลาได้</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>ไม่มีโครงการที่ได้รับสิทธิ์</AlertTitle>
            บัญชีของคุณถูกตั้งค่าให้ลงเวลาเฉพาะโครงการที่กำหนด
            แต่ยังไม่มีโครงการใดถูก assign ให้คุณ
            กรุณาติดต่อผู้ดูแลระบบ
          </Alert>
          <Button variant="outlined" size="large" fullWidth onClick={() => history.goBack()} startIcon={<CancelIcon />} sx={{ py: 1.5, borderRadius: 2 }}>
            ย้อนกลับ
          </Button>
        </Box>
      </Card>
    </Box>
  );

  const renderOverlapProjectPicker = () => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card variant="outlined" sx={{ maxWidth: 440, width: '100%', borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 3, textAlign: 'center', color: 'white' }}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: 'rgba(255,255,255,0.2)', mx: 'auto', mb: 2 }}>
            <PlaceIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            เลือกโครงการที่จะลงเวลา
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            พบ {nearbyProjects.length} โครงการในพื้นที่ของคุณ
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          {/* โครงการในพื้นที่ */}
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 1, display: 'block', letterSpacing: 0.5 }}>
            📍 โครงการในพื้นที่ของคุณ
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 2.5 }}>
            {nearbyProjects.map((item) => (
              <Paper
                key={item.project?._id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  borderColor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main', boxShadow: '0 2px 8px rgba(79,172,254,0.3)' },
                }}
                onClick={() => setProjectSelect(item.project)}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PlaceIcon color="primary" fontSize="small" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {item.project?.project_number || 'ไม่ระบุเลขโครงการ'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.project?.name || '-'}
                    </Typography>
                  </Box>
                  <Chip label={item.distanceText} size="small" color="primary" variant="outlined" />
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Button variant="outlined" size="large" fullWidth onClick={() => history.goBack()} startIcon={<CancelIcon />} sx={{ mt: 1, py: 1.5, borderRadius: 2 }}>
            ย้อนกลับ
          </Button>
        </Box>
      </Card>
    </Box>
  );

  const renderCheckPageCheckin = () => (
    <div>
      {noAssignedProjects
        ? renderNoAssignedProjects()
        : locationLoading
          ? renderLocationLoading()
          : isOutOfLocation
            ? (projectAccessMode ? renderProjectAccessOutOfLocation() : renderTimeStampCardWithOutLocation())
            : (hasOverlap && !projectSelect)
              ? renderOverlapProjectPicker()
              : renderCard()}
    </div>
  );

  if (
    !project?.isLoading &&
    project?.isCompleted &&
    !info?.isLoading &&
    info?.isCompleted
  ) {
    if (!isGeolocationAvailable || !isGeolocationEnabled) {
      return renderGPSRequired();
    }
    return (
      <div>
        {useLocationMode ? (
          loading ? (
            <Loading loading />
          ) : (
            renderCheckPageCheckin()
          )
        ) : loading ? (
          <Loading loading />
        ) : (
          renderOldCard()
        )}
        {renderRequestModal()}
      </div>
    );
  }
  return <Loading isLoading />;
}
